import { useState } from "react";
import { useNavigate } from "react-router-dom";
import ProductForm from "../../../components/features/products/ProductForm";
import { alertSuccess, alertServerError, alertError } from "../../../helpers/alertError";
import PageLayout from "../../../layouts/PageLayout";
import useCustomParams from "../../../hooks/params/useCustomParams";
import PageContainer from "../../../components/ui/PageContainer";
import { useCategories, useProduct, useToken } from "@cabukaka/hooks";
import { uploadService } from "@cabukaka/services";
import { BaseVariant, ProductFormData } from "@cabukaka/types";

export default function UpdateProductPage() {
    const navigate = useNavigate();
    const { id } = useCustomParams();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { product, loading, updateProduct } = useProduct({ id: id! });
    const { categories } = useCategories();
    const { token } = useToken();

    const handleSubmit = async (formData: ProductFormData) => {
        if (!id || !token) {
            alertError("Une erreur s'est produite");
            return;
        }
        setIsSubmitting(true);
        try {
            let productImages = product?.images || [];

            if (formData.localImages && formData.localImages.length > 0) {
                const newImages = await uploadService.uploadMultiple(formData.localImages, token);
                productImages = [...productImages, ...newImages];
            }

            let variantsWithImages: BaseVariant[] | undefined;

            if (formData.variantsFormData && formData.variantsFormData.length > 0) {
                variantsWithImages = await Promise.all(
                    formData.variantsFormData.map(async (variant) => {
                        let variantImages: string[] = variant.existingImages || [];
                        if (variant.localImages && variant.localImages.length > 0) {
                            const newImages = await uploadService.uploadMultiple(variant.localImages, token);
                            variantImages = [...variantImages, ...newImages];
                        }
                        return {
                            name: variant.name,
                            value: variant.value,
                            originalPrice: variant.originalPrice,
                            price: variant.price,
                            description: variant.description,
                            stock: variant.stock,
                            images: variantImages
                        };
                    })
                );
            }

            const submitData = {
                name: formData.name,
                description: formData.description,
                priceMode: formData.priceMode,
                unit: formData.unit,
                categoryId: formData.categoryId,
                subcategoryId: formData.subcategoryId,
                originalPrice: formData.originalPrice,
                price: formData.price,
                inStock: formData.inStock,
                stock: formData.stock,
                images: productImages,
                baseVariants: variantsWithImages
            };

            await updateProduct({
                id: id,
                ...submitData
            });
            alertSuccess("Produit modifié avec succès");
            navigate(-1);
        } catch (error: any) {
            console.error(error);
            alertServerError(error);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (loading) {
        return (
            <PageLayout title="Modifier le produit">
                <div className="flex justify-center py-20">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
                </div>
            </PageLayout>
        );
    }

    if (!product) {
        return (
            <PageLayout title="Modifier le produit">
                <div className="text-center py-20 text-gray-500">Produit non trouvé</div>
            </PageLayout>
        );
    }

    // Transformer les variantes pour le formulaire
    // UpdateProductPage.tsx (partie transformation des données)
    const initialVariantsFormData = product.variants?.map((variant: any) => ({
        name: variant.name,
        value: variant.value,
        originalPrice: variant.originalPrice || '',
        price: variant.price,
        description: variant.description || '',
        stock: variant.stock,
        localImages: [],
        existingImages: variant.images || []
    }));


    const initialData = {
        ...product,
        name: product.name,
        description: product.description || '',
        priceMode: product.priceMode,
        unit: product.unit,
        categoryId: product.categoryId,
        subcategoryId: product.subcategoryId || '',
        originalPrice: product.originalPrice || '',
        price: product.price,
        inStock: product.inStock,
        stock: product.stock,
        localImages: [],
        variantsFormData: initialVariantsFormData
    };

    return (
        <PageLayout title="Modifier le produit">
            <PageContainer>
                <ProductForm
                    initialData={initialData}
                    onSubmit={handleSubmit}
                    isSubmitting={isSubmitting}
                    categories={categories}
                />
            </PageContainer>
        </PageLayout>
    );
}